import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { FileFilled, File } from '@factorialco/f0-react/icons/app'
import {
  AtsVacanciesTableDocument,
  AtsVacanciesTableItemFragment,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import {
  getDaysOpen,
  getStatusConfig,
  renderDeadline,
  renderPerson,
} from './tableHelpers'

export const VacanciesTable = () => {
  const source = useGraphqlDataCollectionSource({
    document: AtsVacanciesTableDocument,
    mapData: (data) => data.ats.vacanciesConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.vacanciesConnection.totalCount ?? 0,
    }),
    mapVariables: ({ search }) => ({ search }),
    search: { enabled: true },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('ats.vacancies.table.columns.name'),
                render: (item: AtsVacanciesTableItemFragment) => item.name,
              },
              {
                label: i18n.t('ats.vacancies.table.columns.recruiter'),
                render: (item: AtsVacanciesTableItemFragment) =>
                  renderPerson(item.requisition?.recruiter?.employee),
              },
              {
                label: i18n.t('ats.vacancies.table.columns.hiring_manager'),
                render: (item: AtsVacanciesTableItemFragment) =>
                  renderPerson(item.requisition?.hiringManager?.employee),
              },
              {
                label: i18n.t('ats.vacancies.table.columns.line_manager'),
                render: (item: AtsVacanciesTableItemFragment) => renderPerson(item.lineManager),
              },
              {
                label: i18n.t('ats.vacancies.table.columns.job_opening'),
                render: (item: AtsVacanciesTableItemFragment) => item.jobPosting?.title ?? '-',
              },
              {
                label: i18n.t('ats.vacancies.table.columns.status'),
                render: (item: AtsVacanciesTableItemFragment) => {
                  const config = getStatusConfig(item.status)
                  return config
                    ? {
                        type: 'status' as const,
                        value: { label: config.label, status: config.status },
                      }
                    : '-'
                },
              },
              {
                label: i18n.t('ats.vacancies.table.columns.days_open'),
                render: (item: AtsVacanciesTableItemFragment) => {
                  const days = getDaysOpen(item.processStartedAt)
                  return days !== null
                    ? { type: 'tag' as const, value: { label: String(days) } }
                    : '-'
                },
              },
              {
                label: i18n.t('ats.vacancies.table.columns.team'),
                render: (item: AtsVacanciesTableItemFragment) =>
                  item.team?.name
                    ? { type: 'tag' as const, value: { label: item.team.name } }
                    : '-',
              },
              {
                label: i18n.t('ats.vacancies.table.columns.market'),
                render: (item: AtsVacanciesTableItemFragment) =>
                  item.location?.name
                    ? { type: 'tag' as const, value: { label: item.location.name } }
                    : '-',
              },
              {
                label: i18n.t('ats.vacancies.table.columns.deadline'),
                render: (item: AtsVacanciesTableItemFragment) => renderDeadline(item.deadline),
              },
              {
                label: i18n.t('ats.vacancies.table.columns.vacancy_type'),
                render: (item: AtsVacanciesTableItemFragment) => {
                  if (!item.vacancyType) return '-'
                  const color: 'flubber' | 'purple' = item.vacancyType === 'new_position' ? 'flubber' : 'purple'
                  return {
                    type: 'dotTag' as const,
                    value: {
                      color,
                      label:
                        item.vacancyType === 'new_position'
                          ? i18n.t('ats.vacancies.table.vacancy_type.new_position')
                          : i18n.t('ats.vacancies.table.vacancy_type.backfill'),
                    },
                  }
                },
              },
              {
                label: i18n.t('ats.vacancies.table.columns.origin'),
                render: (item: AtsVacanciesTableItemFragment) =>
                  item.originType
                    ? {
                        type: 'tag' as const,
                        value: {
                          label:
                            item.originType === 'requisition'
                              ? i18n.t('ats.vacancies.table.origin_type.requisition')
                              : i18n.t('ats.vacancies.table.origin_type.manual'),
                          icon: item.originType === 'requisition' ? FileFilled : File,
                        },
                      }
                    : '-',
              },
              {
                label: i18n.t('ats.vacancies.table.columns.candidate_employee'),
                render: (item: AtsVacanciesTableItemFragment) => renderPerson(item.employee),
              },
            ],
          },
        },
      ]}
    />
  )
}
